import type { ReactNode } from 'react'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'
import styles from './AlertBanner.module.scss'

/**
 * AlertBanner — the ONE banner for notices, warnings, and errors.
 *
 * WHAT: a tinted, bordered card with an icon, optional bold title, a
 * message, and an optional action button. Three variants:
 *   notice  — teal, informational (checkout heads-ups: approvals pending,
 *             same-day turnover). Never an alarm tone.
 *   warning — amber (turnover warnings on order detail, COI required,
 *             degraded transaction rows).
 *   danger  — red (payment failed, submit failures).
 *
 * WHY one component: checkout and order detail each grew bespoke banner
 * markup + style blocks that drifted apart (GG-998 killed six of them).
 * If a surface needs a banner, it uses THIS one — a new one-off style
 * block is the failure mode, not a shortcut.
 *
 * Icons: each variant has a default (Info / AlertTriangle / AlertCircle);
 * pass `icon` when the semantic differs (pending states show a Clock).
 * Mirrors apps/mobile/src/components/AlertBanner — keep the two APIs in
 * lockstep when adding props.
 */

type AlertBannerVariant = 'notice' | 'warning' | 'danger'

const DEFAULT_ICONS: Record<
  AlertBannerVariant,
  typeof AlertTriangle
> = {
  notice: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
}

type AlertBannerProps = {
  variant?: AlertBannerVariant
  /** Bold label. Omit for single-sentence notices. */
  title?: string
  /** The message body. */
  children: ReactNode
  /** Button text — omit to hide the button. */
  actionLabel?: string
  onAction?: () => void
  /** Disable the button and swap the label for a pending state. */
  actionLoading?: boolean
  actionLoadingLabel?: string
  /**
   * Text of the dismiss control — omit for a banner that cannot be closed
   * (every banner before the become-a-vendor announcement). Quieter than
   * the action on purpose: closing is never the main thing to do. Web only
   * so far: mobile's AlertBanner gains this pair when a screen needs it.
   */
  dismissLabel?: string
  onDismiss?: () => void
  dismissTestID?: string
  /** Replaces the variant's default icon (e.g. Clock on pending notices). */
  icon?: ReactNode
  /** ARIA role — 'status' for heads-ups, 'alert' for failures. */
  role?: 'status' | 'alert'
  testID?: string
}

const AlertBanner = ({
  variant = 'warning',
  title,
  children,
  actionLabel,
  onAction,
  actionLoading = false,
  actionLoadingLabel,
  dismissLabel,
  onDismiss,
  dismissTestID,
  icon,
  role,
  testID,
}: AlertBannerProps) => {
  const DefaultIcon = DEFAULT_ICONS[variant]

  return (
    <div
      className={`${styles.banner} ${styles[variant]}`}
      role={role}
      data-testid={testID}
    >
      <span className={styles.icon} aria-hidden='true'>
        {icon ?? <DefaultIcon size={18} />}
      </span>
      <div className={styles.content}>
        {title && <span className={styles.title}>{title}</span>}
        <span className={styles.message}>{children}</span>
      </div>
      {actionLabel && (
        <button
          type='button'
          className={styles.action}
          onClick={onAction}
          disabled={actionLoading}
        >
          {actionLoading ? actionLoadingLabel || actionLabel : actionLabel}
        </button>
      )}
      {dismissLabel && (
        <button type='button' className={styles.dismiss} onClick={onDismiss} data-testid={dismissTestID}>
          {dismissLabel}
        </button>
      )}
    </div>
  )
}

export default AlertBanner
